import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSettings } from '@/lib/api';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState('https://customer-assets-4nw71qhi.emergentagent.net/job_personnel-staffing/artifacts/0igo1bgl_1000283641.png');
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings('footer');
        if (response.data?.value?.logoUrl) {
          setLogoUrl(response.data.value.logoUrl);
        }
      } catch (error) {
        console.error('Error fetching logo settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const navLinks = [
    { href: '/', label: 'Ana Sayfa' },
    { href: '/hizmetler', label: 'Hizmetlerimiz' },
    { 
      href: '/hakkimizda', 
      label: 'Hakkımızda',
      hasDropdown: true,
      subLinks: [
        { href: '/hakkimizda', label: 'Hakkımızda' },
        { href: '/belgelerimiz', label: 'Belgelerimiz' },
        { href: '/referanslarimiz', label: 'Referanslarımız' },
        { href: '/haberler', label: 'Bizden Haberler' },
      ]
    },
    { href: '/is-basvurusu', label: 'İş Başvurusu' },
    { href: '/iletisim', label: 'İletişim' },
  ];

  const isActive = (path) => location.pathname === path;
  const isAboutActive = () => ['/hakkimizda', '/belgelerimiz', '/referanslarimiz', '/haberler'].includes(location.pathname);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      <nav className="eta-container">
        <div className="flex items-center justify-between h-44">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={logoUrl} 
              alt="Eta Global Group" 
              className="h-40 md:h-48 w-auto object-contain "
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div 
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => setAboutDropdownOpen(true)}
                  onMouseLeave={() => setAboutDropdownOpen(false)}
                >
                  <button
                    data-testid="nav-hakkimizda-dropdown"
                    className={`nav-link text-base font-semibold transition-colors flex items-center gap-1 ${
                      isAboutActive()
                        ? 'text-[#005CB2]'
                        : 'text-slate-700 hover:text-[#005CB2]'
                    }`}
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {aboutDropdownOpen && (
                    <div className="absolute top-full left-0 pt-2 w-48">
                      <div className="bg-white rounded-lg shadow-lg border border-slate-100 py-2 overflow-hidden">
                        {link.subLinks.map((subLink) => (
                          <Link
                            key={subLink.href}
                            to={subLink.href}
                            className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                              isActive(subLink.href)
                                ? 'bg-[#005CB2]/10 text-[#005CB2]'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-[#005CB2]'
                            }`}
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                  className={`nav-link text-base font-semibold transition-colors ${
                    isActive(link.href)
                      ? 'text-[#005CB2]'
                      : 'text-slate-700 hover:text-[#005CB2]'
                  }`}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/iletisim">
              <Button 
                data-testid="nav-contact-btn"
                className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
              >
                <Phone className="w-4 h-4" />
                Bize Ulaşın
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-slate-600 hover:text-[#005CB2]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 mobile-menu-enter">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div key={link.href}>
                    <button
                      onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                      className={`w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                        isAboutActive()
                          ? 'bg-[#005CB2]/10 text-[#005CB2]'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {aboutDropdownOpen && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subLinks.map((subLink) => (
                          <Link
                            key={subLink.href}
                            to={subLink.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                              isActive(subLink.href)
                                ? 'bg-[#005CB2]/10 text-[#005CB2]'
                                : 'text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            {subLink.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.href)
                        ? 'bg-[#005CB2]/10 text-[#005CB2]'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <Link 
                to="/iletisim" 
                onClick={() => setMobileMenuOpen(false)}
                className="mt-2 mx-4"
              >
                <Button className="w-full bg-[#005CB2] hover:bg-[#004B92] text-white gap-2">
                  <Phone className="w-4 h-4" />
                  Bize Ulaşın
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
